import type { ReactNode } from 'react';

import classNames from 'classnames';

import styles from './FooterLayout.module.css';

import { useViewport } from '../../hooks';
import { Flex } from '../Flex';
import { Section } from '../Section';
import { FooterColumn } from './FooterColumn';
import { FooterLinkGroup } from './FooterLinkGroup';
import { FooterLinkItem } from './FooterLinkItem';
import { FooterLinksList } from './FooterLinkList';

export interface FooterLayoutProps {
  /** The content of the footer */
  children: ReactNode;
  /** Optional className for styling */
  className?: string;
  /** Optional className for the container */
  containerClassName?: string;
}

/**
 * A basic footer layout component that provides the foundational structure with
 * basic styling and accessible markup. It is designed to be composed according
 * to the consumer needs but with the option of using the provided pieces:
 * `FooterLayout.Column`, `FooterLayout.LinkGroup`, `FooterLayout.LinksList`,
 * `FooterLayout.LinkItem`
 *
 * @example
 *
 * ```
 * <FooterLayout>
 *   <FooterLayout.Column>
 *     <FooterLayout.LinkGroup>
 *      <FooterLayout.LinksList>
 *        <FooterLayout.LinkItem />
 *        <FooterLayout.LinkItem />
 *        <FooterLayout.LinkItem />
 *     </FooterLayout.LinkGroup>
 *   </FooterLayout.Column>
 * </FooterLayout>;
 * ```
 */
export function FooterLayout({
  children,
  className,
  containerClassName,
}: FooterLayoutProps) {
  const viewport = useViewport();
  const isMobile = !viewport.lg;

  return (
    <Section
      as="footer"
      data-testid="footer-root"
      className={classNames(styles.root, className)}
      backgroundColor="Kale2"
      hSpacing={{ xs: 'md', lg: 'xxl' }}
      vSpacing={{ xs: 'lg', lg: 'xl' }}
    >
      <Flex
        direction="column"
        data-testid="footer-container"
        gap={isMobile ? 'lg' : 'xl'}
        className={classNames(styles.container, containerClassName)}
      >
        {children}
      </Flex>
    </Section>
  );
}

FooterLayout.Column = FooterColumn;
FooterLayout.LinkGroup = FooterLinkGroup;
FooterLayout.LinksList = FooterLinksList;
FooterLayout.LinkItem = FooterLinkItem;
